import { Box, IconButton } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FormikAutocomplete from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { AutocompleteOption } from '@/common/presentation/hooks/useAutocomplete';
import tid from '@/common/application/utils/tid';
import { useState, useMemo } from 'react';
import { useListAvailableExtraSystemsAutocomplete } from '@/raptorjob/application/adapters/useListAvailableExtraSystemsAutocomplete';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { useFormikContext } from 'formik';

interface ExtraSystemRowProps {
  index: number;
  rowId: string;
}

export const ExtraSystemRow: React.FC<ExtraSystemRowProps> = ({ index, rowId }) => {
  const { values, setFieldValue } = useFormikContext<ERPJobFormValues>();

  const filterOptionsLocal = (optionsToFilter: AutocompleteOption[], state: { inputValue: string }) => {
    const input = state.inputValue.trim().toLowerCase();
    if (!input) return optionsToFilter;
    return optionsToFilter.filter((option) => option.label.toLowerCase().includes(input));
  };
  const [searchTerm, setSearchTerm] = useState('');
  const { options, loading: optionsLoading } = useListAvailableExtraSystemsAutocomplete(searchTerm, 0, undefined, 10);

  // Get current extra system for this row
  const currentExtraSystem = values.extraSystems?.[index];

  // Create AutocompleteOption from current extra system for the value prop
  const currentValue: AutocompleteOption | null = useMemo(() => {
    if (!currentExtraSystem || currentExtraSystem.extraSystemId == null) {
      return null;
    }

    // Try to find in options first (if already loaded)
    const foundOption = options.find((opt) => opt.value === currentExtraSystem.extraSystemId);
    if (foundOption) {
      return foundOption;
    }

    // Otherwise create option from form values (for initial display)
    return {
      value: currentExtraSystem.extraSystemId,
      label: currentExtraSystem.name || `Extra System ${currentExtraSystem.extraSystemId}`
    };
  }, [currentExtraSystem, options]);

  // Combine options with current value if not already present
  const combinedOptions = useMemo(() => {
    if (!currentValue) return options;

    // Check if current value is already in options
    const existsInOptions = options.some((opt) => opt.value === currentValue.value);
    if (existsInOptions) return options;

    // Add current value to options so it can be displayed
    return [currentValue, ...options];
  }, [options, currentValue]);

  const removeExtraSystem = (indexToRemove: number) => {
    const currentExtraSystems = values.extraSystems || [];
    const updatedExtraSystems = currentExtraSystems.filter((_, i) => i !== indexToRemove);
    setFieldValue('extraSystems', updatedExtraSystems);
  };

  const updateExtraSystem = (index: number, value: AutocompleteOption | null | undefined) => {
    const currentExtraSystems = values.extraSystems || [];
    const updatedExtraSystems = [...currentExtraSystems];
    if (updatedExtraSystems[index]) {
      updatedExtraSystems[index] = {
        ...updatedExtraSystems[index],
        extraSystemId: value?.value != null ? Number(value.value) : null,
        name: value?.label ?? null
      };
      setFieldValue('extraSystems', updatedExtraSystems);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }} {...tid(`jobs:create:extra-system-row-${index}`)}>
      <Box sx={{ width: '50%' }}>
        <FormikAutocomplete<AutocompleteOption, ERPJobFormValues>
          id={`extraSystemIds_${rowId}`}
          label="Extra System"
          options={combinedOptions}
          optionsloading={optionsLoading}
          filterOptions={filterOptionsLocal}
          onInputChange={(_event: unknown, value: string) => setSearchTerm(value)}
          getOptionLabel={(option: AutocompleteOption) => option.label}
          isOptionEqualToValue={(option: AutocompleteOption, value: AutocompleteOption) => option.value === value.value}
          value={currentValue}
          onChange={(value: AutocompleteOption | AutocompleteOption[] | null) => {
            const selectedValue = Array.isArray(value) ? value[0] : value;
            updateExtraSystem(index, selectedValue);
          }}
          {...tid(`jobs:create:extra-system-autocomplete-${index}`)}
        />
      </Box>
      <IconButton
        onClick={() => removeExtraSystem(index)}
        sx={{ color: 'error.main', mb: 3 }}
        aria-label="Remove line item"
        {...tid(`jobs:create:remove-extra-system-button-${index}`)}
      >
        <DeleteOutlinedIcon />
      </IconButton>
    </Box>
  );
};
